import React, { useEffect, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import multiMonthPlugin from "@fullcalendar/multimonth";
import styles from "../styles/calendario.module.css";
import { IoPeopleOutline, IoRestaurantOutline, IoClose, IoCalendarOutline } from "react-icons/io5";
import { useNavigate, useLocation } from "react-router-dom";

import { useCalendario } from "../hooks/useCalendario";
import { eventOrder } from "../domain/calendarioSort";
import { formatFechaLarga } from "../domain/calendarioDates";
import { getEventClassNames, getAgendaClass } from "../domain/calendarioStyles";
import CalendarioDetalleModal from "../components/CalendarioDetalleModal";

export default function Calendario() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    calendarRef,
    wrapRef,
    tabsRef,

    openSubtab,
    setOpenSubtab,
    items,
    selectedDate,
    isNarrow,

    selectedItem,
    showModal,
    closeModal,
    activeTab,
    setActiveTab,
    modalTheme,

    agendaDelDia,
    participacionesOrdenadas,

    openDetalle,
    onEventClick,
    onDateClick,
    onDatesSet,
    dayCellClassNames,
  } = useCalendario();

  // Al volver de editar un perfil/cacería/evento (botón "Atrás" del navegador),
  // reabrir el modal del elemento del que salimos en vez de dejar el calendario "en blanco".
  useEffect(() => {
    const reopen = location.state?.reopenModal;
    if (reopen?.tipo && reopen?.id) {
      openDetalle({ tipo: reopen.tipo, itemId: reopen.id });
    }
    // Solo al montar: refleja el estado con el que se llegó a esta pantalla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToWithReturn = (path) => {
    if (selectedItem) {
      navigate(".", {
        replace: true,
        state: { reopenModal: { tipo: selectedItem.tipo, id: selectedItem.data.id } },
      });
    }
    navigate(path);
  };

  const headerToolbar = useMemo(
    () =>
      isNarrow
        ? { left: "prev,next", center: "title", right: "today,dayGridMonth,dayGridDay" }
        : { left: "prev,next today", center: "title", right: "multiMonthYear,dayGridMonth,dayGridWeek,dayGridDay" },
    [isNarrow]
  );

  const eventContent = (arg) => {
    const tipo = (arg.event.extendedProps?.tipo || "GEN").toUpperCase();
    const viewType = arg.view.type;

    if (viewType === "multiMonthYear") {
      return (
        <div className={styles["year-line-wrapper"]}>
          <span
            className={[
              styles["year-line"],
              tipo === "CACERIA"
                ? styles["year-line-caceria"]
                : tipo === "EVENTO"
                  ? styles["year-line-evento"]
                  : styles["year-line-generico"],
            ].join(" ")}
          />
        </div>
      );
    }

    if (isNarrow) {
      return (
        <div className={styles["evt-inner-mobile"]}>
          <span
            className={
              tipo === "CACERIA"
                ? styles["dot-caceria"]
                : tipo === "EVENTO"
                  ? styles["dot-evento"]
                  : styles["dot-generico"]
            }
          />
        </div>
      );
    }

    return (
      <div className={styles["evt-inner"]}>
        <span className={styles["evt-bar"]} />
        <span className={styles["evt-text"]}>{arg.event.title}</span>
      </div>
    );
  };

  const eventClassNames = (arg) => getEventClassNames({ styles, event: arg.event });

  return (
    <div className={styles["cal-wrapper"]} ref={wrapRef}>
      <h2 className={styles["cal-title"]}><IoCalendarOutline /> Calendario de Eventos y Cacerías</h2>

      <FullCalendar
        ref={calendarRef}
        key={isNarrow ? "mobile" : "desktop"}
        plugins={[dayGridPlugin, interactionPlugin, multiMonthPlugin]}
        initialView="dayGridMonth"
        headerToolbar={headerToolbar}
        views={{
          multiMonthYear: { type: "multiMonth", duration: { years: 1 }, multiMonthMaxColumns: 3 },
        }}
        locale={esLocale}
        events={items}
        firstDay={1}
        fixedWeekCount={false}
        showNonCurrentDates={true}
        dayMaxEventRows={isNarrow ? false : 4}
        height="auto"
        contentHeight="auto"
        eventContent={eventContent}
        eventClassNames={eventClassNames}
        eventOrder={eventOrder}
        eventClick={onEventClick}
        dateClick={onDateClick}
        datesSet={onDatesSet}
        dayCellClassNames={(arg) => {
          const base = dayCellClassNames(arg);
          // Nota: en tu CSS el selector era .fc-daygrid-day.is-selected o .fc-daygrid-day.<hash>
          // Aquí metemos una clase fija "is-selected" y la estilizas en calendario.module.css
          return base.map((c) => styles[c] || c);
        }}
        eventDidMount={(info) => {
          const t = info.event.extendedProps?.tipo;
          const label = t === "CACERIA" ? "Cacería" : t === "EVENTO" ? "Evento" : "Elemento";
          info.el.setAttribute("title", `${label}: ${info.event.title}`);
        }}
        buttonText={{
          today: "Hoy",
          month: "Mes",
          week: isNarrow ? "Sem" : "Semana",
          day: "Día",
          year: "Año",
        }}
      />

      {/* Agenda inferior */}
      {/* Agenda inferior */}
      <div className={styles.agenda}>
        <div className={styles["agenda-head"]}>
          <div className={styles["agenda-date"]}>
            {new Date(selectedDate).toLocaleDateString("es-ES", {
              weekday: "long",
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </div>
        </div>

        <div className={styles["agenda-body"]}>
          {agendaDelDia.length === 0 ? (
            <div className={styles["agenda-empty"]}>No hay elementos este día.</div>
          ) : (
            <ul className={styles["agenda-list"]}>
              {agendaDelDia.map((ev) => {
                const t = (ev.extendedProps?.tipo || "GEN").toUpperCase();

                return (
                  <li
                    key={ev.id}
                    className={`${styles["agenda-item"]} ${t === "CACERIA"
                      ? styles["ag-caceria"]
                      : t === "EVENTO"
                        ? styles["ag-evento"]
                        : styles["ag-generico"]
                      }`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      onEventClick({ event: { id: ev.id } });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onEventClick({ event: { id: ev.id } });
                      }
                    }}
                  >
                    <span className={styles["ag-bullet"]} aria-hidden="true" />

                    <div className={styles["ag-info"]}>
                      <div className={styles["ag-title"]}>{ev.title}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <CalendarioDetalleModal
          show={showModal}
          selectedItem={selectedItem}
          modalTheme={modalTheme}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          participacionesOrdenadas={participacionesOrdenadas}
          openSubtab={openSubtab}
          setOpenSubtab={setOpenSubtab}
          tabsRef={tabsRef}
          onClose={closeModal}
          onGoToEvento={() => {
            goToWithReturn(`/eventos/${selectedItem.data.id}`);
            closeModal();
          }}
          onGoToPersona={(personaId) => {
            goToWithReturn(`/perfiles/${personaId}`);
            closeModal();
          }}
          onGoToCaceria={(caceriaId) => {
            goToWithReturn(`/cacerias/${caceriaId}`);
            closeModal();
          }}
        />
      </div>

    </div>
  );
}
